import { useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api, errorText } from '../lib/api';
import { useMe } from '../lib/session';
import { fmtTime } from '../lib/format';
import { Button, Card, CardHeader, cx, Empty, ErrorText, Field, Input, PageHeader, Pill, Segmented, Select, Textarea } from '../components/ui';

/** INT-ACC-05: a trader asks to cover an extra symbol; Admins and Master Traders approve or decline. */

interface Req {
  id: string;
  requester: { id: string; name: string };
  instrument: { id: string; symbol: string; displayName: string; market: string } | null;
  symbolText: string | null;
  label: string;
  reason: string;
  status: 'PENDING' | 'APPROVED' | 'REJECTED';
  decisionNote: string | null;
  decidedBy: string | null;
  decidedAt: string | null;
  createdAt: string;
}
interface InstrumentRow {
  id: string;
  symbol: string;
  displayName: string;
  marketId: string;
}
interface MarketRow {
  id: string;
  code: string;
  name: string;
}

const tone = (s: Req['status']) => (s === 'APPROVED' ? 'up' : s === 'REJECTED' ? 'down' : 'warn');
const label = (s: Req['status']) => (s === 'APPROVED' ? 'Approved' : s === 'REJECTED' ? 'Declined' : 'Waiting');
const OTHER = '__other__';

function useInstrumentsByMarket() {
  const markets = useQuery({ queryKey: ['markets'], queryFn: () => api<MarketRow[]>('/markets') });
  const instruments = useQuery({ queryKey: ['instruments', 'all'], queryFn: () => api<InstrumentRow[]>('/instruments') });
  return useMemo(
    () => (markets.data ?? []).map((m) => ({ market: m, instruments: (instruments.data ?? []).filter((i) => i.marketId === m.id) })).filter((g) => g.instruments.length),
    [markets.data, instruments.data],
  );
}

export function MySymbols() {
  const me = useMe();
  const qc = useQueryClient();
  const tz = me.data?.timezone ?? 'UTC';
  const groups = useInstrumentsByMarket();
  const requests = useQuery({ queryKey: ['symbol-requests', 'mine'], queryFn: () => api<Req[]>('/symbol-requests') });
  const [pick, setPick] = useState('');
  const [other, setOther] = useState('');
  const [reason, setReason] = useState('');
  const mine = new Set(me.data?.symbols.map((s) => s.id));
  const waiting = new Set(requests.data?.filter((r) => r.status === 'PENDING' && r.instrument).map((r) => r.instrument!.id));
  const ask = useMutation({
    mutationFn: () => api<Req>('/symbol-requests', { body: pick === OTHER ? { symbolText: other, reason } : { instrumentId: pick, reason } }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['symbol-requests'] });
      setPick('');
      setOther('');
      setReason('');
    },
  });
  const bySymbolMarket = new Map<string, { symbol: string; displayName: string }[]>();
  for (const s of me.data?.symbols ?? []) bySymbolMarket.set(s.marketCode, [...(bySymbolMarket.get(s.marketCode) ?? []), s]);

  return (
    <>
      <PageHeader title="My symbols" sub="The symbols you analyse every day, and requests to cover more" />
      <div className="grid items-start gap-3.5 xl:grid-cols-2">
        <Card>
          <CardHeader title={`Symbols you cover · ${me.data?.symbols.length ?? 0}`} />
          {!me.data?.symbols.length && <Empty>No symbols yet. Ask for one on the right.</Empty>}
          <div className="flex flex-col gap-3 p-4">
            {[...bySymbolMarket.entries()].map(([market, list]) => (
              <div key={market} className="flex flex-wrap items-center gap-1.5">
                <span className="w-14 font-mono text-xs text-muted">{market}</span>
                {list.map((s) => (
                  <span key={s.symbol} title={s.displayName} className="rounded-full border border-line2 bg-raised px-3 py-1 text-sm">
                    {s.symbol}
                  </span>
                ))}
              </div>
            ))}
          </div>
        </Card>

        <Card className="flex flex-col gap-3 p-4">
          <h2 className="m-0 text-[15px] font-semibold">Ask to cover another symbol</h2>
          <p className="m-0 text-sm text-sub">An Admin or Master Trader decides. Once approved it joins your daily analysis checklist.</p>
          <Field label="Symbol">
            <Select value={pick} onChange={(e) => setPick(e.target.value)}>
              <option value="">Choose a symbol…</option>
              {groups.map((g) => (
                <optgroup key={g.market.id} label={`${g.market.code} · ${g.market.name}`}>
                  {g.instruments
                    .filter((i) => !mine.has(i.id))
                    .map((i) => (
                      <option key={i.id} value={i.id} disabled={waiting.has(i.id)}>
                        {i.symbol} — {i.displayName}
                        {waiting.has(i.id) ? ' (already asked)' : ''}
                      </option>
                    ))}
                </optgroup>
              ))}
              <option value={OTHER}>It isn’t in this list…</option>
            </Select>
          </Field>
          {pick === OTHER && (
            <Field label="Symbol code" hint="The reviewer adds it to the instrument list first.">
              <Input value={other} maxLength={20} onChange={(e) => setOther(e.target.value.toUpperCase())} placeholder="e.g. SYS" />
            </Field>
          )}
          <Field label="Why do you want to cover it?">
            <Textarea rows={3} maxLength={1000} value={reason} onChange={(e) => setReason(e.target.value)} />
          </Field>
          <ErrorText error={ask.error ? errorText(ask.error) : null} />
          {ask.isSuccess && <p className="m-0 text-sm text-up">Request sent. You’ll get an alert when it’s decided.</p>}
          <Button variant="primary" className="self-start" disabled={!pick || (pick === OTHER && !other.trim()) || reason.trim().length < 5 || ask.isPending} onClick={() => ask.mutate()}>
            Send request
          </Button>
        </Card>

        <Card className="xl:col-span-2">
          <CardHeader title="My requests" />
          {requests.data && !requests.data.length && <Empty>No requests yet.</Empty>}
          <ul className="m-0 flex list-none flex-col p-0">
            {requests.data?.map((r) => (
              <li key={r.id} className="flex flex-wrap items-start justify-between gap-2 border-t border-[#1a1f27] px-4 py-3 first:border-t-0">
                <div className="flex min-w-0 flex-col gap-0.5">
                  <span className="font-semibold">{r.label}</span>
                  <span className="text-sm text-sub">{r.reason}</span>
                  {r.status !== 'PENDING' && (
                    <span className="text-xs text-muted">
                      {r.status === 'APPROVED' ? 'Approved' : 'Declined'} by {r.decidedBy} · {fmtTime(r.decidedAt, tz, true)}
                      {r.decisionNote ? ` — “${r.decisionNote}”` : ''}
                    </span>
                  )}
                </div>
                <Pill tone={tone(r.status)}>{label(r.status)}</Pill>
              </li>
            ))}
          </ul>
        </Card>
      </div>
    </>
  );
}

export function SymbolRequests() {
  const me = useMe();
  const tz = me.data?.timezone ?? 'UTC';
  const [filter, setFilter] = useState<'PENDING' | 'DONE'>('PENDING');
  const list = useQuery({ queryKey: ['symbol-requests', 'all'], queryFn: () => api<Req[]>('/symbol-requests') });
  const shown = (list.data ?? []).filter((r) => (filter === 'PENDING' ? r.status === 'PENDING' : r.status !== 'PENDING'));
  const pending = (list.data ?? []).filter((r) => r.status === 'PENDING').length;

  return (
    <>
      <PageHeader title="Symbol requests" sub="Traders asking to cover more symbols" right={<Segmented label="Show" value={filter} onChange={setFilter} options={[{ value: 'PENDING', label: `Waiting · ${pending}` }, { value: 'DONE', label: 'Decided' }]} />} />
      <ErrorText error={list.error ? errorText(list.error) : null} />
      <Card>
        {list.data && !shown.length && <Empty>{filter === 'PENDING' ? 'No requests waiting.' : 'Nothing decided yet.'}</Empty>}
        <ul className="m-0 flex list-none flex-col p-0">
          {shown.map((r) => (
            <li key={r.id} className="border-t border-[#1a1f27] first:border-t-0">
              {r.status === 'PENDING' ? (
                <Decide req={r} tz={tz} self={r.requester.id === me.data?.id} />
              ) : (
                <div className="flex flex-wrap items-start justify-between gap-2 px-4 py-3">
                  <div className="flex flex-col gap-0.5">
                    <span className="font-semibold">
                      {r.requester.name} · {r.label}
                    </span>
                    <span className="text-xs text-muted">
                      {label(r.status)} by {r.decidedBy} · {fmtTime(r.decidedAt, tz, true)}
                      {r.decisionNote ? ` — “${r.decisionNote}”` : ''}
                    </span>
                  </div>
                  <Pill tone={tone(r.status)}>{label(r.status)}</Pill>
                </div>
              )}
            </li>
          ))}
        </ul>
      </Card>
    </>
  );
}

function Decide({ req, tz, self }: { req: Req; tz: string; self: boolean }) {
  const qc = useQueryClient();
  const groups = useInstrumentsByMarket();
  const [note, setNote] = useState('');
  const [instrumentId, setInstrumentId] = useState('');
  const decide = useMutation({
    mutationFn: (decision: 'APPROVE' | 'REJECT') => api<Req>(`/symbol-requests/${req.id}/decision`, { body: { decision, note, ...(instrumentId ? { instrumentId } : {}) } }),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ['symbol-requests'] }),
  });
  const needsMapping = !req.instrument;
  return (
    <div className="flex flex-col gap-3 px-4 py-3">
      <div className="flex flex-wrap items-start justify-between gap-2">
        <div className="flex flex-col gap-0.5">
          <span className="font-semibold">
            {req.requester.name} asks to cover {req.label}
          </span>
          <span className="text-xs text-muted">{fmtTime(req.createdAt, tz, true)}</span>
        </div>
        <Pill tone="warn">Waiting</Pill>
      </div>
      <p className="m-0 rounded-lg bg-raised px-3.5 py-2.5 text-sm text-ink2">{req.reason}</p>
      {self ? (
        <p className="m-0 text-sm text-muted">This is your own request — someone else needs to decide it.</p>
      ) : (
        <div className={cx('grid gap-2', needsMapping ? 'sm:grid-cols-[1fr_1.4fr_auto]' : 'sm:grid-cols-[1.4fr_auto]')}>
          {needsMapping && (
            <Field label={`Which listed symbol is ${req.symbolText}?`} hint="Not listed? Add it under Markets & symbols first.">
              <Select value={instrumentId} onChange={(e) => setInstrumentId(e.target.value)}>
                <option value="">Choose…</option>
                {groups.map((g) => (
                  <optgroup key={g.market.id} label={g.market.code}>
                    {g.instruments.map((i) => (
                      <option key={i.id} value={i.id}>
                        {i.symbol} — {i.displayName}
                      </option>
                    ))}
                  </optgroup>
                ))}
              </Select>
            </Field>
          )}
          <Field label="Note to the trader" hint="Required when you decline.">
            <Input value={note} maxLength={1000} onChange={(e) => setNote(e.target.value)} />
          </Field>
          <div className="flex items-start gap-2 pt-5">
            <Button variant="success" disabled={decide.isPending || (needsMapping && !instrumentId)} onClick={() => decide.mutate('APPROVE')}>
              Approve
            </Button>
            <Button variant="danger" disabled={decide.isPending || note.trim().length < 3} onClick={() => decide.mutate('REJECT')}>
              Decline
            </Button>
          </div>
        </div>
      )}
      <ErrorText error={decide.error ? errorText(decide.error) : null} />
    </div>
  );
}
